import React, { useState, useContext, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    AlertColor,
    Box,
    Chip
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { UserContextType, useAuth } from '../auth';
import { baseBackendUrl } from '../service/baseService';

const Emitter = require("tiny-emitter/instance");

interface SendNotificationModalProps {
    open: boolean;
    onClose: () => void;
}

const SendNotificationModal: React.FC<SendNotificationModalProps> = ({ open, onClose }) => {
    const { getToken } = useContext<UserContextType>(useAuth);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<AlertColor>('info');
    const [sending, setSending] = useState(false);
    const [recipientCount, setRecipientCount] = useState<number | null>(null);

    useEffect(() => {
        if (open) {
            fetchRecipientCount();
        }
    }, [open]);

    const fetchRecipientCount = async () => {
        try {
            const token = getToken ? getToken() : '';
            const response = await fetch(`${baseBackendUrl}/api/Notification/recipient-count`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setRecipientCount(data.count);
            }
        } catch (error) {
            console.error('Failed to fetch recipient count:', error);
        }
    };

    const handleSend = async () => {
        if (!message) return;

        setSending(true);
        try {
            const token = getToken ? getToken() : '';
            const response = await fetch(`${baseBackendUrl}/api/Notification/broadcast`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: message,
                    severity: severity
                })
            });

            if (response.ok) {
                Emitter.emit('notify', 'Notification sent successfully', 'success');
                onClose();
                setMessage('');
                setSeverity('info');
            } else {
                Emitter.emit('notify', 'Failed to send notification', 'error');
            }
        } catch (error) {
            Emitter.emit('notify', 'An error occurred', 'error');
            console.error(error);
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Send Site Notification</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, mt: 1 }}>
                    <Chip
                        icon={<PeopleIcon />}
                        label={recipientCount !== null ? `${recipientCount} recipient${recipientCount !== 1 ? 's' : ''} will receive this notification` : 'Loading recipient count...'}
                        color="primary"
                        sx={{ fontSize: '0.9rem', py: 2.5, px: 1 }}
                    />
                </Box>
                <TextField
                    autoFocus
                    margin="dense"
                    id="message"
                    label="Message"
                    type="text"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <FormControl fullWidth>
                    <InputLabel id="severity-label">Priority / Severity</InputLabel>
                    <Select
                        labelId="severity-label"
                        id="severity"
                        value={severity}
                        label="Priority / Severity"
                        onChange={(e) => setSeverity(e.target.value as AlertColor)}
                    >
                        <MenuItem value="info">Info (Blue)</MenuItem>
                        <MenuItem value="success">Success (Green)</MenuItem>
                        <MenuItem value="warning">Warning (Orange)</MenuItem>
                        <MenuItem value="error">Error (Red)</MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSend} variant="contained" color="primary" disabled={sending}>
                    {sending ? 'Sending...' : 'Broadcast'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendNotificationModal;
